import { Component, ErrorInfo, ReactNode } from 'react';
import { error as logError } from '@/lib/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for Clerk authentication failures
 * Catches script loading errors and auth initialization failures
 */
export class ClerkErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError('Clerk Error Boundary caught an error:', error, { componentStack: errorInfo.componentStack });
  }

  public componentDidMount() {
    // Listen for unhandled promise rejections (catches Clerk loading failures)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  public componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    // Check if this is a Clerk-related error
    const errorMessage = event.reason?.message || String(event.reason);
    const isClerkError = errorMessage.includes('Clerk') ||
                         errorMessage.includes('clerk') ||
                         errorMessage.includes('Unauthorized') && window.location.href.includes('clerk');

    if (isClerkError) {
      event.preventDefault(); // Prevent default logging
      this.setState({
        hasError: true,
        error: event.reason instanceof Error ? event.reason : new Error(errorMessage),
      });
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          backgroundColor: '#17171A',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '32px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              textAlign: 'center',
            }}>
              🔐
            </div>

            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '16px',
              textAlign: 'center',
              // Vault Black on this white card. Brand cream (#F5F2EC) here was
              // ~1.05:1 -- the heading was invisible. Same defect as
              // ConvexErrorBoundary; both were recoloured for a dark surface
              // that this card never adopted.
              color: '#111113',
            }}>
              Authentication Service Error
            </h1>

            <p style={{
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '24px',
              textAlign: 'center',
              color: '#A6AAB0',
            }}>
              We're having trouble loading the authentication service. This may be due to a network issue or ad blocker.
            </p>

            <div style={{
              padding: '12px',
              backgroundColor: '#241C08',
              border: '1px solid #F2B713',
              borderRadius: '4px',
              marginBottom: '16px',
            }}>
              <p style={{ margin: 0, color: '#F2B713', fontSize: '14px' }}>
                <strong>Common Causes:</strong><br />
                • <strong>401 Unauthorized:</strong> Wrong publishable key or domain not allowed<br />
                • Using test key (pk_test_*) in production environment<br />
                • Domain not added to Clerk Dashboard allowed origins<br />
                • Ad blocker or network issue preventing connection
              </p>
            </div>

            {this.state.error?.message.includes('401') && (
              <div style={{
                padding: '12px',
                backgroundColor: '#2A1113',
                border: '1px solid #B54B4B',
                borderRadius: '4px',
                marginBottom: '16px',
              }}>
                <p style={{ margin: 0, color: '#F1A1A1', fontSize: '14px' }}>
                  <strong>⚠️ Configuration Issue Detected</strong><br />
                  This appears to be a Clerk API key or domain configuration problem.<br />
                  Please check your environment variables and Clerk Dashboard settings.
                </p>
              </div>
            )}

            <button
              onClick={this.handleReload}
              style={{
                width: '100%',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: '#F2B713',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#D19E0B';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#F2B713';
              }}
            >
              Reload Page
            </button>

            {this.state.error && (
              <details style={{
                marginTop: '24px',
                fontSize: '14px',
                color: '#A6AAB0',
              }}>
                <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                  Technical Details
                </summary>
                <pre style={{
                  backgroundColor: '#17171A',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px',
                }}>
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
